import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { itineraryEditApi } from '../../services/api';

const PLACEHOLDER_TEXTS = [
  'Too many museums...',
  'Add more street food...',
  'Make mornings lighter...',
  'More outdoor activities...',
  'Add a sunset spot...',
];

/* ─── Styled Components ─── */

const InputWrapper = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(255, 255, 255, 0.96);
  }
`;

const InputRow = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-radius: 9999px;
  padding: 4px 4px 4px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: #1f2937;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #9ca3af;
  }
`;

const SendButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${props => props.disabled ? '#e5e7eb' : 'linear-gradient(135deg, #4285F4 0%, #1A73E8 100%)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0 4px 0;
  font-size: 12px;
  color: #6b7280;
`;

const NudgeInput = ({ itineraryId, userId, onUpdate }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setPlaceholderIdx(prev => (prev + 1) % PLACEHOLDER_TEXTS.length);
    }, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    try {
      const response = await itineraryEditApi.editItinerary(itineraryId, userId, {
        instruction: trimmed,
      });
      setText('');
      onUpdate?.(response.data);
    } catch (err) {
      // error handled by caller or toast
    } finally {
      setLoading(false);
    }
  };

  return (
    <InputWrapper>
      <InputRow onSubmit={handleSubmit}>
        <StyledInput
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={PLACEHOLDER_TEXTS[placeholderIdx]}
          disabled={loading}
          aria-label="Nudge input for itinerary adjustments"
        />
        <SendButton type="submit" disabled={!text.trim() || loading} aria-label="Send nudge">
          {loading ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
              </svg>
            </motion.div>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </SendButton>
      </InputRow>
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <LoadingDots>Adjusting your itinerary...</LoadingDots>
          </motion.div>
        )}
      </AnimatePresence>
    </InputWrapper>
  );
};

export default NudgeInput;
